import React, { useEffect, useState } from "react";
import Main from "../Components/Main";
import PageContainer from "../Components/PageContainer";
import { useToast } from "../Components/toast/ToastProvider";
import CollapseCard from "../Components/CollapseCard";
import { Button, Table } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "react-feather";
import { formatDate } from "../config/function";
import { getBillProcessByDate } from "../Service/BillProcessService";
import { getAllVendors } from "../Service/VendorService";
import { getAllVehicles } from "../Service/VehicleService";

const PaymentPreProcessing = () => {
  const [paymentSummary, setPaymentSummary] = useState([]);
  const [processDate, setProcessDate] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const toast = useToast();

  useEffect(() => {
    fetchPaymentSummary();
    fetchVendors();
    fetchVehicles();
  }, []);

  const fetchVendors = async () => {
    try {
      const res = await getAllVendors();
      setVendors(res.data || []);
    } catch (err) {
      toast(err.message || "Failed to Fetch Vendors", "ERROR");
    }
  };

  const fetchVehicles = async () => {
    try {
      const res = await getAllVehicles();
      setVehicles(res.data || []);
    } catch (err) {
      toast(err.message || "Failed to Fetch Vehicles", "ERROR");
    }
  };

  const fetchPaymentSummary = async (date) => {
    if (!date) {
      setPaymentSummary([]);
      return;
    }
    try {
      const res = await getBillProcessByDate(date);

      const formatted = res.bills.map((b) => {
        const normalList = res.normal_advances.filter(
          (a) => a.vendor_id === b.vendor_id,
        );

        const receiptList = res.receipt_advances.filter(
          (a) => a.vendor_id === b.vendor_id,
        );

        const selectedNormal =
          b.status === "process"
            ? normalList.find((a) => a.id === b.nor_advance_id)
            : normalList.length === 1
              ? normalList[0]
              : null;

        const selectedReceipt =
          b.status === "process"
            ? receiptList.find((a) => a.id === b.res_advance_id)
            : receiptList.length === 1
              ? receiptList[0]
              : null;

        return {
          bill_id: b.bill_id,
          vendor_id: b.vendor_id,
          vehicle_id: b.vehicle_id,
          trip_count: b.trip_count,
          total_amount: b.total_amount,
          status: b.status,

          normal_advances: normalList,
          nor_advance_id: selectedNormal?.id || null,
          nor_advance: selectedNormal?.amount || 0,
          nor_remaining_amount: selectedNormal?.nor_remaining_amount || 0,
          nor_advance_deduction: b.nor_advance_deduction || 0,

          receipt_advances: receiptList,
          res_advance_id: selectedReceipt?.id || null,
          res_advance: selectedReceipt?.amount || 0,
          res_remaining_amount: selectedReceipt?.res_remaining_amount || 0,
          res_advance_deduction: b.res_advance_deduction || 0,
        };
      });

      setPaymentSummary(formatted);
    } catch (err) {
      toast(err.message || "Failed to fetch payment summary", "ERROR");
    }
  };

  const addPDFHeader = (doc, pageWidth) => {
    const logo = new Image();
    logo.src = "/img/asa_logo.png";

    const logoWidth = 18;
    const logoHeight = 18;
    const spacing = 5;

    const headerText = "ASA Transport";
    const subHeaderText = "Mannargudi";

    const headerWidth = doc.getTextWidth(headerText);
    const subHeaderWidth = doc.getTextWidth(subHeaderText);
    const totalWidth =
      logoWidth + spacing + Math.max(headerWidth, subHeaderWidth);
    const startX = (pageWidth - totalWidth) / 2;

    try {
      doc.addImage(logo, "PNG", startX, 8, logoWidth, logoHeight);
    } catch (e) {
      console.warn("Logo load skipped (optimization)");
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(headerText, startX + logoWidth + spacing, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subHeaderText, startX + logoWidth + spacing, 20);
  };

  const handleDownloadPDF = () => {
    if (paymentSummary.length === 0) {
      toast("No data to download", "ERROR");
      return;
    }
    const capitalize = (str) => {
      if (!str) return "N/A";
      return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const safeFixed = (value) => {
      let num = parseFloat(value);
      if (isNaN(num)) return "0.00";
      return num.toFixed(2);
    };

    const doc = new jsPDF({ orientation: "potrait", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();

    addPDFHeader(doc, pageWidth);

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Payment Pre-Processing", pageWidth / 2, 35, { align: "center" });

    let y = 45;
    doc.setFontSize(10);
    doc.text(`Date: ${formatDate(processDate)}`, 14, y);

    const tableColumn = [
      "S.No",
      "Vendor",
      "Trip Count",
      "Trip Balance",
      "Normal Advance",
      "Normal Advance Balance",
      "Normal Advance Deduction",
      "Receipt Advance",
      "Receipt Advance Balance",
      "Receipt Advance Deduction",
    ];

    const tableRows = paymentSummary.map((pay, index) => {
      const vendorName = vendors.find((v) => v.id === pay.vendor_id);

      return [
        index + 1,
        capitalize(vendorName?.name || "N/A"),
        pay.trip_count || 0,
        safeFixed(pay.total_amount),

        safeFixed(pay.nor_advance),
        safeFixed(pay.nor_remaining_amount),
        pay.nor_advance_deduction || "",

        safeFixed(pay.res_advance),
        safeFixed(pay.res_remaining_amount),
        pay.res_advance_deduction || "",
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: y + 10,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        fontStyle: "bold",
      },
    });

    const fileName = `Payment_PreProcessing_${processDate}.pdf`;
    doc.save(fileName);

    const pdfBlob = doc.output("blob");
    const fileSizeKB = (pdfBlob.size / 1024).toFixed(2);
  };

  return (
    <Main>
      <PageContainer>
        <CollapseCard title="Payment Pre-Processing">
          <div style={{ marginBottom: 15 }}>
            <label>Date:</label>
            <input
              type="date"
              value={processDate}
              onChange={(e) => {
                const value = e.target.value;
                setProcessDate(value);
                fetchPaymentSummary(value);
              }}
            />
          </div>
          <Table size="sm">
            <thead>
              <tr>
                <th>S.no</th>
                <th>Vendor</th>
                <th>Trip Count</th>
                <th>Trip Balance Amount</th>
                <th>Normal Advance</th>
                <th>Normal Advance Balance</th>
                <th>Recepit Advance</th>
                <th>Recepit Advance Balance</th>
              </tr>
            </thead>
            <tbody>
              {paymentSummary.length > 0 &&
                paymentSummary.map((pay, index) => {
                  const vendorName = vendors.find(
                    (v) => v.id === pay.vendor_id,
                  );

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td style={{ textTransform: "capitalize" }}>
                        {vendorName?.name || "N/A"}
                      </td>
                      <td>{pay.trip_count}</td>
                      <td>{pay.total_amount.toFixed(2)}</td>
                      <td>
                        {pay.status === "process" ? (
                          Number(pay.nor_advance || 0).toFixed(2)
                        ) : pay.normal_advances.length === 0 ? (
                          "—"
                        ) : pay.normal_advances.length === 1 ? (
                          Number(pay.nor_advance || 0).toFixed(2)
                        ) : (
                          <select
                            value={pay.nor_advance_id || ""}
                            onChange={(e) => {
                              const adv = pay.normal_advances.find(
                                (a) => a.id === Number(e.target.value),
                              );

                              const updated = [...paymentSummary];
                              updated[index].nor_advance_id = adv.id;
                              updated[index].nor_advance = adv.amount;
                              updated[index].nor_remaining_amount =
                                adv.nor_remaining_amount;
                              setPaymentSummary(updated);
                            }}
                          >
                            <option value="">Select</option>
                            {pay.normal_advances.map((a) => (
                              <option key={a.id} value={a.id}>
                                Adv: {a.amount}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      <td>
                        {Number(pay.nor_remaining_amount || 0).toFixed(2)}
                      </td>
                      <td>
                        {pay.status === "process" ? (
                          Number(pay.res_advance || 0).toFixed(2)
                        ) : pay.receipt_advances.length === 0 ? (
                          "—"
                        ) : pay.receipt_advances.length === 1 ? (
                          Number(pay.res_advance || 0).toFixed(2)
                        ) : (
                          <select
                            value={pay.res_advance_id || ""}
                            onChange={(e) => {
                              const adv = pay.receipt_advances.find(
                                (a) => a.id === Number(e.target.value),
                              );

                              const updated = [...paymentSummary];
                              updated[index].res_advance_id = adv.id;
                              updated[index].res_advance = adv.amount;
                              updated[index].res_remaining_amount =
                                adv.res_remaining_amount;
                              setPaymentSummary(updated);
                            }}
                          >
                            <option value="">Select</option>
                            {pay.receipt_advances.map((a) => (
                              <option key={a.id} value={a.id}>
                                Adv: {a.amount}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      <td>
                        {Number(pay.res_remaining_amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </CollapseCard>
        <div className="d-flex justify-content-end mb-2 mt-2">
          <Button variant="success" size="sm" onClick={handleDownloadPDF}>
            <Download />
            Download PDF
          </Button>
        </div>
      </PageContainer>
    </Main>
  );
};

export default PaymentPreProcessing;
